import {Box, Flex, Link, Stack, Text, useSlotRecipe} from '@chakra-ui/react3'
import {FormattedMessage, useIntl} from 'react-intl'
import StyledContent from '../../../../../leap/components/styled-content'
import Asset from '../../../../../leap/components/cms/asset'
import React, {useEffect, useMemo, useRef} from 'react'
import useOcapiSite from '../../../../../hooks/useOcapiSite'
import PropTypes from 'prop-types'
import howToUseRecipe from './how-to-use.recipe'

const DocumentLink = ({documentString, ...props}) => {
    const {httpsCatalogContentUrl} = useOcapiSite()
    const pipe = documentString.indexOf('|')
    const url =
        httpsCatalogContentUrl &&
        httpsCatalogContentUrl.stokkeMasterCatalog &&
        `${httpsCatalogContentUrl.stokkeMasterCatalog}/${documentString.substring(0, pipe)}`
    const linkText = documentString.substring(pipe + 1, documentString.length).replace('|', '. ')
    return (
        <Link
            href={url}
            variant="secondary"
            underline
            // documents are PDFs on the content catalog - open them alongside the PDP.
            // `rel` matches the leap CTA convention (`useCtaAction`) for new-tab links
            target="_blank"
            rel="noopener noreferrer"
            {...props}
        >
            <span>{linkText}</span>
        </Link>
    )
}

DocumentLink.propTypes = {
    documentString: PropTypes.string
}

const HowToUse = ({isExpanded, product}) => {
    const recipe = useSlotRecipe({recipe: howToUseRecipe})
    const styles = useMemo(() => recipe(), [recipe])
    const {formatMessage} = useIntl()
    const assemblyVideoIds = useMemo(
        () =>
            (product?.c_assemblyVideos?.map((video) => video?.split('|')?.[0]) || []).filter(
                Boolean
            ),
        [product?.c_assemblyVideos]
    )
    const assemblyVideoAssets = useMemo(
        () =>
            assemblyVideoIds.map((brightcoveId) => ({
                type: 'brightcove',
                videoType: 'video',
                brightcoveId,
                autoplay: false,
                title: formatMessage({
                    defaultMessage: 'Assembly Instructions',
                    id: 'product_detail.after_sales.assembly_instruction'
                })
            })),
        [assemblyVideoIds, formatMessage]
    )
    const assemblyVideoRefs = useRef([])
    const {brightcoveEnabled} = useOcapiSite()

    useEffect(() => {
        // pause all videos when the accordion is collapsed or unmounted
        if (!isExpanded) {
            assemblyVideoRefs.current?.forEach((player) => {
                player?.pause?.()
            })
        }
        // pause all videos when the component is unmounted - we use the same effect
        // as there is no ill effect of additionally pausing when the accordion expands/collapses
        return () => {
            // we want the latest refs when the component is unmounted
            // as any previous refs doesn't matter as they'll either
            // be still in the refs array or not in the DOM
            // eslint-disable-next-line react-hooks/exhaustive-deps
            assemblyVideoRefs.current?.forEach((player) => {
                player?.pause?.()
            })
        }
    }, [isExpanded])

    return (
        <Flex css={styles.container}>
            <Flex css={styles.panel}>
                {product?.c_maintenance && (
                    <Flex css={styles.panelColumn}>
                        <Text as="h3" css={styles.columnHeading}>
                            <FormattedMessage
                                defaultMessage="Maintenance"
                                id="product_detail.after_sales.maintenance"
                            />
                        </Text>
                        <StyledContent content={product.c_maintenance}></StyledContent>
                    </Flex>
                )}
                {product?.c_documents && (
                    <Flex css={styles.panelColumn}>
                        <Text as="h3" css={styles.columnHeading}>
                            <FormattedMessage
                                defaultMessage="Downloads"
                                id="product_detail.after_sales.downloads"
                            />
                        </Text>
                        <Stack css={styles.documents}>
                            {product.c_documents.map((docString) => (
                                <DocumentLink
                                    key={docString}
                                    documentString={docString}
                                    css={styles.documentLink}
                                ></DocumentLink>
                            ))}
                        </Stack>
                    </Flex>
                )}
            </Flex>
            {brightcoveEnabled && assemblyVideoAssets.length > 0 ? (
                <Stack css={styles.assemblyContainer}>
                    <Text as="h3" css={styles.columnHeading}>
                        <FormattedMessage
                            defaultMessage="Assembly Instructions"
                            id="product_detail.after_sales.assembly_instruction"
                        />
                    </Text>
                    {assemblyVideoAssets.map((asset, index) => (
                        <Box key={index + asset.brightcoveId} css={styles.videoItem}>
                            <Asset
                                asset={asset}
                                videoRef={(handle) => {
                                    assemblyVideoRefs.current[index] = handle
                                }}
                            />
                        </Box>
                    ))}
                </Stack>
            ) : null}
        </Flex>
    )
}

HowToUse.propTypes = {
    isExpanded: PropTypes.bool,
    product: PropTypes.object
}

export default HowToUse
